/* What aria-modal promises, kept (designs/issue-tracker.md IT9; Copilot on
   #142). While a modal is open:

   - focus moves into it, and goes back to whatever opened it on close;
   - Tab and Shift+Tab wrap inside it;
   - Escape closes it from anywhere on the page, not only from inside;
   - every other top-level region of the page is inert.

   The modal must be rendered into its own child of <body> (createPortal),
   so "everything else" is exactly its siblings. Only the inert attributes
   this hook set are removed again — a region already inert (the picker's
   own) is left alone. */
import { useEffect, useRef, type RefObject } from "react";

const FOCUSABLE =
  'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

/* of a radio group only the checked member is a tab stop */
export const focusablesIn = (root: HTMLElement): HTMLElement[] =>
  [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (el) =>
      !(
        el instanceof HTMLInputElement &&
        el.type === "radio" &&
        !el.checked &&
        root.querySelector(`input[type="radio"][name="${el.name}"]:checked`)
      ),
  );

export function useModal(ref: RefObject<HTMLElement | null>, onClose: () => void): void {
  /* the latest onClose, without re-running the effect on every render */
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const host = [...document.body.children].find((c) => c.contains(dialog));
    const inerted: Element[] = [];
    for (const el of [...document.body.children])
      if (el !== host && !el.hasAttribute("inert")) {
        el.setAttribute("inert", "");
        inerted.push(el);
      }

    (focusablesIn(dialog)[0] ?? dialog).focus();

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.preventDefault();
        close.current();
        return;
      }
      if (e.key !== "Tab") return;
      const stops = focusablesIn(dialog);
      if (stops.length === 0) return void e.preventDefault();
      const first = stops[0]!;
      const last = stops[stops.length - 1]!;
      const at = document.activeElement;
      const outside = !dialog.contains(at);
      if (e.shiftKey && (at === first || outside)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (at === last || outside)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("keydown", onKey);
      for (const el of inerted) el.removeAttribute("inert");
      if (opener?.isConnected) opener.focus();
    };
  }, [ref]);
}
